/**
 * NAO checkout — configuración del formulario POR PRODUCTO.
 *
 * ============================================================================
 * CÓMO AÑADIR UN PRODUCTO NUEVO AL FORMULARIO
 * ============================================================================
 *
 * 1) En el admin de Shopify abre el producto → «Metacampos» → «NAO · clave de
 *    formulario» (`custom.nao_form_key`) y escribe una clave corta, en
 *    minúsculas y sin espacios. Ej.: `roll-on`.
 *
 * 2) Añade aquí abajo una entrada con ESA MISMA clave. Solo hace falta declarar
 *    lo que se aparte de `_default`: el resto se hereda.
 *
 * 3) Si el producto necesita markup propio (copy del kit, promo del gotero,
 *    modal sí/no), refleja los flags de render en el espejo Liquid que está al
 *    inicio de `blocks/buy-buttons.liquid` — busca el comentario
 *    "ESPEJO DE RENDER". Si te olvidas, no se rompe nada: al inicializar, el
 *    checkout compara ambos lados y avisa por consola de la divergencia.
 *
 * 4) Si además estrenas una REGLA DE ENVÍO nueva, añádela en `SHIPPING_RULES`
 *    (aquí abajo) y replícala en el backend — `api/index.js` del repo
 *    `nao-checkout-backend`, constante `SHIPPING_RULES`. El backend recalcula
 *    el envío por su cuenta y rechaza la orden si no coincide con el front, así
 *    que un cambio a un solo lado hace fallar el checkout de forma visible (no
 *    en silencio, que es lo que queremos).
 *
 * Un producto SIN metacampo, o con una clave que no esté en el mapa, cae en
 * `_default`: formulario plenamente funcional, sin promo de gotero y sin copy
 * del Kit de Inicio. Es deliberado — la promo del gotero es exclusiva del Kit
 * de Inicio y no debe aparecer por accidente en ningún otro producto.
 *
 * Significado de cada flag:
 *   usaModal      El formulario se abre en ventana emergente. `false` = va
 *                 embebido (inline) en la página de producto. Hoy todos los
 *                 productos usan `true`: el embebido sigue soportado (snippet,
 *                 estilos y píxel) pero ningún producto lo activa.
 *   regaloGotero  Muestra la promo del gotero de regalo, la alerta de pérdida
 *                 y el modal de aversión a la pérdida. Solo Kit de Inicio.
 *   selectorAroma El producto se pide eligiendo aromas con cantidad por
 *                 aroma (una línea de pedido por cada uno). Sustituye al
 *                 selector de cantidad único y al `variant-picker` nativo.
 *   copyKit       Copy propio del Kit de Inicio (encabezado, desglose).
 *   cantidad      Muestra el selector de cantidad dentro del formulario.
 *   envio         Clave de `SHIPPING_RULES` que decide cómo se cobra el envío.
 */

// Reglas de envío. Cada una debe existir igual en el backend (ver cabecera).
export const SHIPPING_RULES = {

    // Kit de Inicio: el envío depende del método de pago. Anticipado va gratis;
    // contraentrega suma el recargo, que es el diferencial de la oferta.
    gratis_anticipado_cod_aparte: {
        modo: 'por_metodo_pago',
        prepago: 0,
        contraentrega: 14000
    },

    // Roll-on: el envío depende de las UNIDADES, no del método de pago.
    // 1 a 3 unidades cuestan envío; a partir de `gratisDesde` es gratis.
    escalonado_rollon: {
        modo: 'por_cantidad',
        costo: 18000,
        gratisDesde: 4
    }
};

export const NAO_FORM_CONFIG = {

    'kit-inicio': {
        usaModal: true,
        regaloGotero: true,
        selectorAroma: false,
        copyKit: true,
        cantidad: false,
        envio: 'gratis_anticipado_cod_aparte'
    },

    'roll-on': {
        usaModal: true,
        regaloGotero: false,
        selectorAroma: true,
        copyKit: false,
        cantidad: true,
        envio: 'escalonado_rollon'
    },

    _default: {
        usaModal: true,
        regaloGotero: false,
        selectorAroma: false,
        copyKit: false,
        cantidad: false,
        envio: 'gratis_anticipado_cod_aparte'
    }
};

// Devuelve la config efectiva de una clave, con `_default` como base. Nunca
// lanza ni devuelve undefined: una clave desconocida cae entera en `_default`.
export function resolveFormConfig(key) {

    const base = NAO_FORM_CONFIG._default;
    const specific = (key && NAO_FORM_CONFIG[key]) || null;

    return Object.assign({}, base, specific || {});
}

/**
 * Calcula el costo de envío en pesos (no centavos).
 * Es la ÚNICA función que decide el envío en el front; el backend replica esta
 * misma lógica y rechaza la orden si los dos números no coinciden.
 */
export function computeShipping(config, options) {

    const rule = SHIPPING_RULES[config && config.envio];

    // Regla desconocida: se cobra 0 antes que inventar un cargo al cliente.
    if (!rule) return 0;

    const isCOD = !!(options && options.isCOD);
    const quantity = Math.max(1, parseInt((options && options.quantity) || 1, 10) || 1);

    if (rule.modo === 'por_cantidad') {
        return quantity >= rule.gratisDesde ? 0 : rule.costo;
    }

    return isCOD ? rule.contraentrega : rule.prepago;
}

// ¿Esta configuración da envío gratis al llegar a cierta cantidad? Lo usa la UI
// para saber si debe mostrar la explicación del kit y desde qué unidad.
export function freeShippingThreshold(config) {

    const rule = SHIPPING_RULES[config && config.envio];

    if (!rule || rule.modo !== 'por_cantidad') return null;

    return rule.gratisDesde;
}

/**
 * Lee la clave de producto del DOM y compara los flags de render que emitió
 * Liquid contra los de este mapa. Si divergen, avisa por consola: significa que
 * alguien tocó un lado y no el otro (ver punto 3 de la cabecera).
 */
export function configFromWrapper(wrapper) {

    const key = (wrapper && wrapper.dataset.naoFormKey) || '';
    const config = resolveFormConfig(key);

    try {

        const rendered = wrapper && wrapper.dataset.naoRenderFlags;

        if (rendered) {

            const flags = rendered.split(',');

            const mismatch = ['usaModal', 'regaloGotero', 'copyKit', 'cantidad', 'selectorAroma']
                .filter((flag, index) => String(config[flag]) !== flags[index]);

            if (mismatch.length) {
                console.warn(
                    '[NAO] La config de render (buy-buttons.liquid) y NAO_FORM_CONFIG ' +
                    'no coinciden para la clave "' + key + '": ' + mismatch.join(', ')
                );
            }
        }

    } catch (error) {
        // La comprobación es un diagnóstico, nunca puede tumbar el checkout.
    }

    return config;
}
